/**
 * Диаграмма workflow-переходов Jira (накопленный граф) для настройки маппинга.
 *
 * Самодостаточный SVG без внешних библиотек: узлы раскладываются по колонкам
 * категории (new → indeterminate → done), рёбра рисуются стрелками. Рёбра,
 * требующие экран Jira (hasScreen), показаны пунктиром — Lens не может их
 * выполнить автоматически. Замапленные целевые статусы подсвечены.
 */
import { useMemo } from 'react';
import { Empty, Typography } from 'antd';
import type { JiraTransitionGraph } from '@lens/shared';

const BOX_W = 140;
const BOX_H = 36;
const COL_GAP = 80;
const ROW_GAP = 18;
const PAD = 16;

/** Порядок колонок по ключу категории Jira. Неизвестные категории — в середину. */
const CATEGORY_ORDER: Record<string, number> = {
  new: 0,
  undefined: 1,
  indeterminate: 1,
  done: 2,
};

const CATEGORY_COLOR: Record<string, string> = {
  new: '#8c8c8c',
  indeterminate: '#1677ff',
  done: '#52c41a',
};

interface NodeLayout {
  name: string;
  category: string;
  x: number;
  y: number;
}

export function TransitionGraphDiagram({
  graph,
  mappedTargets,
}: {
  graph: JiraTransitionGraph;
  mappedTargets: string[];
}): JSX.Element {
  const layout = useMemo(() => {
    // Узлы графа = объявленные узлы ∪ концы рёбер (на случай, если статусы
    // проекта не подгрузились, но рёбра уже выучены).
    const catByName = new Map<string, string>();
    for (const n of graph.nodes) catByName.set(n.name, n.category);
    for (const e of graph.edges) {
      if (!catByName.has(e.fromStatus)) catByName.set(e.fromStatus, 'undefined');
      if (!catByName.has(e.toStatus)) catByName.set(e.toStatus, 'undefined');
    }

    const columns = new Map<number, string[]>();
    for (const [name, category] of catByName) {
      const col = CATEGORY_ORDER[category] ?? 1;
      if (!columns.has(col)) columns.set(col, []);
      columns.get(col)!.push(name);
    }

    const nodes = new Map<string, NodeLayout>();
    let maxRows = 0;
    for (const [col, names] of [...columns.entries()].sort((a, b) => a[0] - b[0])) {
      names.sort((a, b) => a.localeCompare(b));
      maxRows = Math.max(maxRows, names.length);
      names.forEach((name, row) => {
        nodes.set(name, {
          name,
          category: catByName.get(name) ?? 'undefined',
          x: PAD + col * (BOX_W + COL_GAP),
          y: PAD + row * (BOX_H + ROW_GAP),
        });
      });
    }

    const numCols = columns.size || 1;
    const width = PAD * 2 + numCols * BOX_W + (numCols - 1) * COL_GAP;
    const height = PAD * 2 + Math.max(maxRows, 1) * BOX_H + Math.max(maxRows - 1, 0) * ROW_GAP;
    return { nodes, width, height };
  }, [graph]);

  if (graph.edges.length === 0 && graph.nodes.length === 0) {
    return (
      <Empty
        image={Empty.PRESENTED_IMAGE_SIMPLE}
        description="Граф переходов пуст — просканируйте workflow по нескольким задачам"
      />
    );
  }

  const targetSet = new Set(mappedTargets);

  return (
    <div style={{ overflowX: 'auto', border: '1px solid var(--border-color, #f0f0f0)', borderRadius: 8, padding: 8 }}>
      <svg
        width={layout.width}
        height={layout.height}
        viewBox={`0 0 ${layout.width} ${layout.height}`}
        role="img"
        aria-label="Диаграмма переходов статусов Jira"
      >
        <defs>
          <marker id="tg-arrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#bfbfbf" />
          </marker>
          <marker id="tg-arrow-screen" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
            <path d="M0,0 L10,5 L0,10 z" fill="#ff7875" />
          </marker>
        </defs>

        {/* Рёбра — кривые безье, чтобы читались как связи, а не прямые через боксы. */}
        {graph.edges.map((e, i) => {
          const from = layout.nodes.get(e.fromStatus);
          const to = layout.nodes.get(e.toStatus);
          if (!from || !to) return null;
          const forward = to.x > from.x;
          let d: string;
          if (forward) {
            // Слева направо: выходим из правой грани источника, входим в левую грань цели.
            const x1 = from.x + BOX_W;
            const y1 = from.y + BOX_H / 2;
            const x2 = to.x;
            const y2 = to.y + BOX_H / 2;
            const dx = Math.max(40, (x2 - x1) / 2);
            d = `M ${x1} ${y1} C ${x1 + dx} ${y1}, ${x2 - dx} ${y2}, ${x2} ${y2}`;
          } else {
            // Та же колонка / «назад»: уводим дугой вправо, входим в правую грань цели,
            // чтобы линия не накладывалась на боксы и стрелка была видна.
            const x1 = from.x + BOX_W;
            const y1 = from.y + BOX_H / 2;
            const x2 = to.x + BOX_W;
            const y2 = to.y + BOX_H / 2;
            const bow = 48;
            d = `M ${x1} ${y1} C ${x1 + bow} ${y1}, ${x2 + bow} ${y2}, ${x2} ${y2}`;
          }
          return (
            <path
              key={`e-${i}`}
              d={d}
              fill="none"
              stroke={e.hasScreen ? '#ff7875' : '#bfbfbf'}
              strokeWidth={1.5}
              strokeDasharray={e.hasScreen ? '4 3' : undefined}
              markerEnd={e.hasScreen ? 'url(#tg-arrow-screen)' : 'url(#tg-arrow)'}
            />
          );
        })}

        {/* Узлы */}
        {[...layout.nodes.values()].map((n) => {
          const isTarget = targetSet.has(n.name);
          const accent = CATEGORY_COLOR[n.category] ?? '#8c8c8c';
          return (
            <g key={`n-${n.name}`}>
              <rect
                x={n.x}
                y={n.y}
                width={BOX_W}
                height={BOX_H}
                rx={6}
                fill={isTarget ? '#e6f4ff' : '#ffffff'}
                stroke={isTarget ? '#1677ff' : accent}
                strokeWidth={isTarget ? 2 : 1}
              />
              <rect x={n.x} y={n.y} width={4} height={BOX_H} rx={2} fill={accent} />
              <text
                x={n.x + BOX_W / 2 + 2}
                y={n.y + BOX_H / 2}
                textAnchor="middle"
                dominantBaseline="central"
                fontSize={12}
                fill="#262626"
              >
                {n.name.length > 16 ? `${n.name.slice(0, 15)}…` : n.name}
              </text>
            </g>
          );
        })}
      </svg>
      <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
        Синим — целевые статусы маппинга. Пунктир — переход требует экран Jira (не выполняется автоматически).
      </Typography.Text>
    </div>
  );
}
